import {DateValue, parseAbsolute} from '@internationalized/date';
import {Fragment, memo} from 'react';
import {useDateFormatter} from '@ui/i18n/use-date-formatter';
import {shallowEqual} from '@ui/utils/shallow-equal';
import {useSettings} from '@ui/settings/use-settings';
import {useUserTimezone} from '@ui/i18n/use-user-timezone';
import {dateOnlyToUtcIso, isDateOnlyString} from '@ui/i18n/date-only';

export const DateFormatPresets: Record<
  'numeric' | 'short' | 'long' | 'timestamp' | 'time',
  Intl.DateTimeFormatOptions
> = {
  numeric: {year: 'numeric', month: '2-digit', day: '2-digit'},
  short: {year: 'numeric', month: 'short', day: '2-digit'},
  long: {month: 'long', day: '2-digit', year: 'numeric'},
  timestamp: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  },
  time: {
    hour: '2-digit',
    minute: '2-digit',
  },
};

interface FormattedDateProps {
  date?: string | DateValue | Date;
  options?: Intl.DateTimeFormatOptions;
  preset?: keyof typeof DateFormatPresets;
  timezone?: string;
}

export const FormattedDate = memo(
  ({date, options, preset, timezone: propsTimezone}: FormattedDateProps) => {
    const {dates} = useSettings();
    const userTimezone = useUserTimezone();

    // Konu 12: salt-tarih değerleri ("2005-08-29") bir takvim günüdür, zaman
    // anı değil. UTC'ye sabitlenip UTC olarak biçimlendirilirler; aksi halde
    // ziyaretçinin saat dilimi günü kaydırır.
    const dateOnly = isDateOnlyString(date);
    const timezone = dateOnly
      ? 'UTC'
      : propsTimezone || options?.timeZone || userTimezone;

    // Kopya alınıyor: aksi halde çağıranın `options` nesnesine timeZone
    // yazılır ve aynı nesneyi paylaşan diğer çağrılara sızar.
    const formatterOptions: Intl.DateTimeFormatOptions = {
      ...(options ||
        (DateFormatPresets as Record<string, Intl.DateTimeFormatOptions>)[
          preset || dates?.format || 'short'
        ] ||
        {}),
    };

    if (dateOnly || !formatterOptions.timeZone) {
      formatterOptions.timeZone = timezone;
    }

    const formatter = useDateFormatter(formatterOptions);

    if (!date) {
      return null;
    }

    // make sure date with invalid format does not blow up the app
    try {
      if (typeof date === 'string') {
        date = parseAbsolute(
          dateOnly ? dateOnlyToUtcIso(date) : date,
          timezone,
        ).toDate();
      } else if ('toDate' in date) {
        date = date.toDate(timezone);
      }
    } catch (e) {
      return null;
    }

    return <Fragment>{formatter.format(date as Date)}</Fragment>;
  },
  shallowEqual,
);
